import { createSlice } from '@reduxjs/toolkit';
import { request } from 'modules/Api/HttpClient';
import {
  APPLICATION_TOKENS_URL,
  APPLICATION_TOKEN_BY_ID_URL,
  APPLICATION_TOKENS_CREATE_URL,
  APPLICATION_TOKENS_UPDATE_URL,
  APPLICATION_TOKENS_DELETE_URL,
  APPLICATION_TOKENS_REGENERATE_URL,
} from 'modules/Api/Routes';

const initialState = {
  loading: false,
  error: null,
  data: {
    applicationTokens: [],
    total: 0,
    page: 1,
    perPage: 10,
    search: '',
    active: null,
    sort: {
      sortType: 'DESC',
      sortBy: 'created_at',
    },
  },
  isDeleting: false,
  isRegenerating: false,
};

const applicationTokensSlice = createSlice({
  name: 'applicationTokens',
  initialState,
  reducers: {
    cleanState: () => ({ ...initialState }),
    changeSort: (state, action) => {
      const newSort = action.payload;
      state.data.sort.sortType =
        state.data.sort.sortBy === newSort && state.data.sort.sortType === 'ASC'
          ? 'DESC'
          : 'ASC';
      state.data.sort.sortBy = action.payload;
    },
    requestApplicationTokens: (state) => {
      state.loading = true;
      state.error = null;
    },
    changeSearch: (state, action) => {
      state.data.search = action.payload;
    },
    changePage: (state, action) => {
      state.data.page = action.payload;
    },
    changePerPage: (state, action) => {
      state.data.perPage = action.payload;
      state.data.page = 1;
    },
    changeActiveFilter: (state, action) => {
      state.data.active = action.payload;
      state.data.page = 1;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setIsDeleting: (state, action) => {
      state.isDeleting = action.payload;
    },
    setIsRegenerating: (state, action) => {
      state.isRegenerating = action.payload;
    },
    receiveRequestSuccess: (state) => {
      state.loading = false;
    },
    receiveApplicationTokensList: (state, action) => {
      state.loading = false;
      state.data = {
        ...state.data,
        applicationTokens: action.payload.application_tokens,
        total: action.payload.total_items,
      };
    },
    clearApplicationTokensList: (state) => {
      state.loading = false;
      state.data = {
        applicationTokens: [],
        total: 0,
        page: 1,
        perPage: 10,
        search: '',
        active: null,
        sort: {
          sortType: 'DESC',
          sortBy: 'created_at',
        },
      };
    },
    receiveApplicationTokensError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    changeApplicationTokensFilters: (state, action) => {
      state.data.page = isNaN(action?.payload?.page)
        ? 1
        : Number(action?.payload?.page);
      state.data.perPage = isNaN(action?.payload?.paginates_per)
        ? state.data.perPage
        : Number(action?.payload?.paginates_per);
      state.data.search = action?.payload.search || '';
      state.data.active = action?.payload.active ?? null;
    },
    updateApplicationTokenOnList: (state, action) => {
      const updToken = action.payload;
      const index = state.data.applicationTokens.findIndex(
        (token) => token.id === updToken.id
      );
      if (index >= 0) state.data.applicationTokens.splice(index, 1, updToken);
    },
  },
});

const Actions = applicationTokensSlice.actions;

const Selectors = {
  fetchListData: (state) => state.applicationTokens,
  applicationTokensLoading: ({ applicationTokens: { loading } }) => ({
    loading,
  }),
};

const Async = {
  fetchApplicationTokensList: () => async (dispatch, getState) => {
    const {
      applicationTokens: {
        data: {
          page,
          perPage,
          search,
          active,
          sort: { sortType, sortBy },
        },
      },
    } = getState();

    let action;

    dispatch(Actions.requestApplicationTokens());

    try {
      const params = {
        page,
        paginates_per: perPage,
        search,
        sort: sortType,
        sort_by: sortBy,
      };

      if (active !== null && active !== undefined && active !== '') {
        params.active = active;
      }

      const response = await request({
        method: 'GET',
        url: APPLICATION_TOKENS_URL,
        params,
      });

      action = Actions.receiveApplicationTokensList(response.data.content);
    } catch (e) {
      action = Actions.receiveApplicationTokensError(e.message);
    }

    dispatch(action);
  },

  getApplicationTokenById:
    ({ id, onSuccess, onError }) =>
    async () => {
      try {
        const response = await request({
          method: 'GET',
          url: `${APPLICATION_TOKEN_BY_ID_URL}?id=${id}`,
        });

        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  createApplicationToken:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestApplicationTokens());
      try {
        const response = await request({
          method: 'POST',
          url: APPLICATION_TOKENS_CREATE_URL,
          data: {
            name: data.name,
            expires: data.expires,
            expires_at: data.expires ? data.expires_at : null,
          },
        });

        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveApplicationTokensError());
        onError(e);
      }
    },

  updateApplicationToken:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestApplicationTokens());
      try {
        const response = await request({
          method: 'PUT',
          url: APPLICATION_TOKENS_UPDATE_URL,
          data: {
            id: data.id,
            name: data.name,
            expires: data.expires,
            expires_at: data.expires ? data.expires_at : null,
          },
        });

        const updatedToken = response.data.content;
        dispatch(Actions.updateApplicationTokenOnList(updatedToken));
        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveApplicationTokensError());
        onError(e);
      }
    },

  regenerateApplicationToken:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.setIsRegenerating(true));

      try {
        const response = await request({
          method: 'PATCH',
          url: APPLICATION_TOKENS_REGENERATE_URL,
          data: { id },
        });

        const updatedToken = response.data.content;
        dispatch(Actions.updateApplicationTokenOnList(updatedToken));
        dispatch(Actions.setIsRegenerating(false));
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.setIsRegenerating(false));
        onError(e);
      }
    },

  deleteApplicationToken:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.setIsDeleting(true));

      try {
        const response = await request({
          method: 'DELETE',
          url: APPLICATION_TOKENS_DELETE_URL,
          data: { id },
        });

        onSuccess(response);
        dispatch(Actions.setIsDeleting(false));
      } catch (e) {
        onError(e);
        dispatch(Actions.setIsDeleting(false));
      }
    },
};

const reducer = applicationTokensSlice.reducer;

export { reducer, Actions, Async, Selectors };
